// The signed-in user, for the shell chrome (TopBar chip, Home greeting,
// Tasks "Mine" matching). Replaces the prototype's hardcoded Jason Lopez.
//
// Identity comes from the MSAL account claims (no network). The avatar
// photo comes from Microsoft Graph (helpdesk pattern): a delegated
// User.Read token + GET /me/photos/48x48/$value → blob URL. 404 = user has
// no photo — fall back to initials. The photo fetch runs once per session
// (module-level cache), not per component mount.

import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { markSignedOut, signedInAccount } from './ssoMemory';

export interface CurrentUser {
  displayName: string;
  firstName: string;
  /** e.g. "C. White" — the TopBar short form. */
  shortName: string;
  initials: string;
  email: string;
  /** Blob object-URL for the Graph photo; null until loaded / if none. */
  photoUrl: string | null;
}

let photoCache: { forUsername: string; url: string | null } | null = null;
let photoInFlight: Promise<string | null> | null = null;

export function useCurrentUser(): CurrentUser {
  const { instance, accounts } = useMsal();
  // The ACTIVE account, never simply the first cached (auth/ssoMemory.ts).
  const account = signedInAccount(instance, accounts);
  const [photoUrl, setPhotoUrl] = useState<string | null>(
    photoCache && photoCache.forUsername === account?.username ? photoCache.url : null,
  );

  const identity = useMemo(() => {
    const displayName = account?.name ?? account?.username ?? '';
    const email = account?.username ?? '';
    const parts = displayName.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? displayName;
    const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    const shortName = lastName ? `${firstName[0]}. ${lastName}` : firstName;
    const initials =
      ((firstName[0] ?? '') + (lastName[0] ?? '')).toUpperCase() || email.slice(0, 2).toUpperCase();
    return { displayName, firstName, shortName, initials, email };
  }, [account]);

  useEffect(() => {
    if (!account) return;
    if (photoCache && photoCache.forUsername === account.username) {
      setPhotoUrl(photoCache.url);
      return;
    }
    let cancelled = false;
    photoInFlight ??= (async () => {
      try {
        let accessToken: string;
        try {
          ({ accessToken } = await instance.acquireTokenSilent({
            account,
            scopes: ['User.Read'],
          }));
        } catch (err) {
          // Fresh scope in this session's token cache → silent fails with
          // interaction_required and, uncorrected, the chrome shows initials
          // forever (same silent-failure class as the directory-search bug,
          // 2026-07-13). One popup rescues it; after that, silent works.
          if (!(err instanceof InteractionRequiredAuthError)) throw err;
          ({ accessToken } = await instance.acquireTokenPopup({ account, scopes: ['User.Read'] }));
        }
        const res = await fetch('https://graph.microsoft.com/v1.0/me/photos/96x96/$value', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          if (res.status !== 404) console.warn(`[useCurrentUser] Graph photo fetch: HTTP ${res.status}`);
          return null; // 404 = no photo set
        }
        return URL.createObjectURL(await res.blob());
      } catch (err) {
        // Popup blocked or network — the chrome degrades to initials.
        console.warn('[useCurrentUser] Graph photo fetch failed:', err);
        return null;
      }
    })();
    void photoInFlight.then((url) => {
      photoCache = { forUsername: account.username, url };
      if (!cancelled) setPhotoUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [instance, account]);

  return { ...identity, photoUrl };
}

/** Sign the user out of this app (and the Entra session for it). A CHOICE:
 *  remembered, so no link signs them straight back in (auth/ssoMemory.ts) —
 *  until they sign in again. No account is passed on purpose: MSAL then
 *  clears every cached account, which the localStorage cache now needs. */
export function useSignOut(): () => void {
  const { instance } = useMsal();
  return () => {
    markSignedOut();
    void instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };
}
